import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Sparkles,
  Wallet,
  TrendingDown,
  TrendingUp,
  History,
  AlertTriangle,
  ShoppingCart,
  Gift,
  ArrowUpCircle,
  ArrowDownCircle,
  RefreshCcw,
  Undo2,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { PermissionGate } from '@/components/PermissionGate';
import { Permission } from '@/common/enums/permissions.enum';
import {
  aiCreditsTenantService,
  AiCreditBalance,
  AiCreditPackage,
  AiCreditTransaction,
} from '@/features/branding/services/ai-credits.service';

export const Route = createFileRoute('/_authenticated/admin/ai-credits')({
  component: AiCreditsRoute,
});

function AiCreditsRoute() {
  return (
    <PermissionGate permission={Permission.ADMIN_TENANT}>
      <AiCreditsPage />
    </PermissionGate>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatUsd(value: number) {
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatBrl(value: number) {
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(value: string) {
  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const transactionMeta: Record<
  AiCreditTransaction['type'],
  { label: string; icon: typeof ArrowUpCircle; className: string; sign: '+' | '-' }
> = {
  purchase: {
    label: 'Compra',
    icon: ArrowUpCircle,
    className: 'text-green-600',
    sign: '+',
  },
  debit: {
    label: 'Consumo',
    icon: ArrowDownCircle,
    className: 'text-red-600',
    sign: '-',
  },
  adjustment: {
    label: 'Ajuste',
    icon: RefreshCcw,
    className: 'text-blue-600',
    sign: '+',
  },
  refund: {
    label: 'Estorno',
    icon: Undo2,
    className: 'text-green-600',
    sign: '+',
  },
};

// ─── Página principal ─────────────────────────────────────────────────────────

function AiCreditsPage() {
  const queryClient = useQueryClient();
  const [purchasePackage, setPurchasePackage] = useState<AiCreditPackage | null>(null);

  const { data: balance, isLoading: balanceLoading } = useQuery<AiCreditBalance>({
    queryKey: ['ai-credits', 'balance'],
    queryFn: () => aiCreditsTenantService.getMyBalance(),
  });

  const { data: packages = [], isLoading: packagesLoading } = useQuery<AiCreditPackage[]>({
    queryKey: ['ai-credits', 'packages'],
    queryFn: () => aiCreditsTenantService.listPackages(),
  });

  const { data: transactions = [], isLoading: transactionsLoading } = useQuery<
    AiCreditTransaction[]
  >({
    queryKey: ['ai-credits', 'transactions'],
    queryFn: () => aiCreditsTenantService.getMyTransactions(20),
  });

  const activePackages = packages
    .filter((p) => p.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  if (balanceLoading) {
    return (
      <div className="p-6 max-w-5xl mx-auto space-y-4 animate-pulse">
        <div className="h-8 w-56 bg-muted rounded" />
        <div className="h-32 bg-muted rounded-xl" />
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="h-56 bg-muted rounded-xl" />
          <div className="h-56 bg-muted rounded-xl" />
          <div className="h-56 bg-muted rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8">
      {/* Cabeçalho */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Sparkles className="h-6 w-6" />
          Créditos de IA
        </h1>
        <p className="text-muted-foreground mt-1">
          Gerencie o saldo pré-pago usado pelas funcionalidades de Inteligência Artificial.
        </p>
      </div>

      {/* Alerta de saldo zerado */}
      {balance?.blocked && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/40 p-4 text-sm text-red-800 dark:text-red-300">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <p>
            Seu saldo de créditos de IA está zerado. As funcionalidades de IA (análise de leads,
            Gemini, etc.) ficarão indisponíveis até que você adicione créditos.
          </p>
        </div>
      )}

      {/* Resumo do saldo */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Wallet className="h-5 w-5 text-primary" />
            Saldo atual
          </CardTitle>
          <CardDescription>
            Os custos das chamadas de IA são debitados automaticamente em dólar (USD).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="rounded-lg border p-4">
              <p className="text-sm text-muted-foreground">Saldo disponível</p>
              <p
                className={`text-2xl font-bold mt-1 ${
                  balance?.blocked ? 'text-red-600' : 'text-foreground'
                }`}
              >
                US$ {formatUsd(balance?.balanceUsd ?? 0)}
              </p>
              {balance?.blocked && (
                <Badge variant="destructive" className="mt-2">
                  Saldo zerado
                </Badge>
              )}
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <TrendingUp className="h-3.5 w-3.5" />
                Total comprado
              </p>
              <p className="text-2xl font-bold mt-1">
                US$ {formatUsd(balance?.totalPurchasedUsd ?? 0)}
              </p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <TrendingDown className="h-3.5 w-3.5" />
                Total consumido
              </p>
              <p className="text-2xl font-bold mt-1">
                US$ {formatUsd(balance?.totalSpentUsd ?? 0)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pacotes de créditos */}
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Adicionar créditos</h2>
          <p className="text-sm text-muted-foreground">
            Escolha um pacote para recarregar seu saldo de IA. O pagamento é simulado e o crédito
            é aplicado imediatamente.
          </p>
        </div>

        {packagesLoading ? (
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="h-56 bg-muted rounded-xl animate-pulse" />
            <div className="h-56 bg-muted rounded-xl animate-pulse" />
            <div className="h-56 bg-muted rounded-xl animate-pulse" />
          </div>
        ) : activePackages.length === 0 ? (
          <div className="rounded-lg border bg-muted/40 p-6 text-center text-sm text-muted-foreground">
            Nenhum pacote de créditos disponível no momento.
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-3 items-stretch">
            {activePackages.map((pkg) => (
              <PackageCard key={pkg.id} pkg={pkg} onSelect={() => setPurchasePackage(pkg)} />
            ))}
          </div>
        )}
      </div>

      {/* Extrato */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <History className="h-5 w-5" />
          Extrato recente
        </h2>

        {transactionsLoading ? (
          <div className="h-48 bg-muted rounded-xl animate-pulse" />
        ) : transactions.length === 0 ? (
          <div className="rounded-lg border bg-muted/40 p-6 text-center text-sm text-muted-foreground">
            Nenhuma movimentação registrada ainda.
          </div>
        ) : (
          <div className="rounded-lg border divide-y">
            {transactions.map((tx) => {
              const meta = transactionMeta[tx.type] ?? transactionMeta.adjustment;
              const Icon = meta.icon;
              return (
                <div key={tx.id} className="flex items-center justify-between gap-3 p-3 text-sm">
                  <div className="flex items-center gap-3 min-w-0">
                    <Icon className={`h-4 w-4 flex-shrink-0 ${meta.className}`} />
                    <div className="min-w-0">
                      <p className="font-medium truncate">
                        {meta.label}
                        {tx.module ? ` · ${tx.module}` : ''}
                      </p>
                      {tx.description && (
                        <p className="text-xs text-muted-foreground truncate">
                          {tx.description}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">{formatDate(tx.createdAt)}</p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`font-semibold ${meta.className}`}>
                      {meta.sign} US$ {formatUsd(Math.abs(Number(tx.amountUsd)))}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      saldo: US$ {formatUsd(Number(tx.balanceAfterUsd))}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {purchasePackage && (
        <PurchaseDialog
          pkg={purchasePackage}
          onClose={() => setPurchasePackage(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['ai-credits'] });
            setPurchasePackage(null);
          }}
        />
      )}
    </div>
  );
}

// ─── Card de pacote ────────────────────────────────────────────────────────────

function PackageCard({ pkg, onSelect }: { pkg: AiCreditPackage; onSelect: () => void }) {
  const baseUsd = Number(pkg.usdAmount);
  const bonusUsd = Number(pkg.bonusUsd);
  const totalUsd = baseUsd + bonusUsd;

  return (
    <Card className="flex flex-col">
      <CardHeader className="text-center pb-2 pt-6">
        <CardTitle className="text-xl">{pkg.label}</CardTitle>
        <CardDescription>
          <span className="text-3xl font-bold text-foreground">
            R$ {formatBrl(Number(pkg.priceBrl))}
          </span>
        </CardDescription>
      </CardHeader>

      <CardContent className="flex-1 space-y-3">
        <Separator />
        <div className="space-y-2.5 text-sm">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary flex-shrink-0" />
            <span>
              <strong>US$ {formatUsd(baseUsd)}</strong> em créditos de IA
            </span>
          </div>
          {bonusUsd > 0 && (
            <div className="flex items-center gap-2">
              <Gift className="h-4 w-4 text-primary flex-shrink-0" />
              <span>
                + <strong>US$ {formatUsd(bonusUsd)}</strong> de bônus
              </span>
            </div>
          )}
          {pkg.description && <p className="text-muted-foreground">{pkg.description}</p>}
        </div>
        <Separator />
        <div className="text-sm flex justify-between">
          <span className="text-muted-foreground">Total de créditos</span>
          <span className="font-semibold">US$ {formatUsd(totalUsd)}</span>
        </div>
      </CardContent>

      <CardFooter>
        <Button className="w-full gap-2" onClick={onSelect}>
          <ShoppingCart className="h-4 w-4" />
          Comprar pacote
        </Button>
      </CardFooter>
    </Card>
  );
}

// ─── Dialog de compra (pagamento simulado) ────────────────────────────────────

function PurchaseDialog({
  pkg,
  onClose,
  onSuccess,
}: {
  pkg: AiCreditPackage;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const baseUsd = Number(pkg.usdAmount);
  const bonusUsd = Number(pkg.bonusUsd);
  const totalUsd = baseUsd + bonusUsd;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const result = await aiCreditsTenantService.purchase(pkg.id);
      toast.success(
        `Pacote ${pkg.label} adquirido! Novo saldo: US$ ${formatUsd(result.balanceUsd)}`,
      );
      onSuccess();
    } catch {
      // o interceptor do axios já mostra o toast de erro
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !loading && onClose()}>
      <DialogContent size="md" busy={loading}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-primary" />
            Comprar créditos de IA
          </DialogTitle>
          <DialogDescription>
            Confirme os detalhes abaixo. A cobrança é simulada (sem gateway de pagamento real) e
            o crédito é aplicado imediatamente ao seu saldo.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-lg border bg-muted/40 p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Pacote</span>
              <span className="font-medium">{pkg.label}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Créditos base</span>
              <span className="font-medium">US$ {formatUsd(baseUsd)}</span>
            </div>
            {bonusUsd > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Bônus</span>
                <span className="font-medium">US$ {formatUsd(bonusUsd)}</span>
              </div>
            )}
            <Separator />
            <div className="flex justify-between text-base font-semibold">
              <span>Total de créditos</span>
              <span>US$ {formatUsd(totalUsd)}</span>
            </div>
            <div className="flex justify-between text-base font-semibold">
              <span>Valor cobrado</span>
              <span>R$ {formatBrl(Number(pkg.priceBrl))}</span>
            </div>
          </div>
        </div>

        <DialogFooter className="flex flex-wrap gap-2 justify-end">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} loading={loading} className="gap-2">
            Confirmar compra
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
